import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { IPopup } from 'app/shared/elements/popup-base';
import { validate } from 'app/shared/elements/ess-rule';
import { Toaster } from 'app/shared/lib/toaster';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { MemberService } from 'app/cd/services/member-service';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { deepClone } from 'app/shared/lib/utilities';
import * as CD from 'app/cd/models/essette-api';

@autoinject()
export class MemberProviderRelationshipsPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected validateForm = validate;
    protected relationshipFormIsValid: boolean = false;
    protected relationships: ReadonlyArray<CD.MemberProviderRelationship> | null = null;
    protected editedRelationship: CD.MemberProviderRelationship = {};

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;
    protected includeInactive: boolean = false;

    ////****************** Element References ***************************************////
    private $relationshipModal!: IPopup;
    protected $relationshipForm!: IForm;
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: MemberService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'description',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected async activate(): Promise<void> {
        this.reload();
    }


    ////****************** Events ***************************************************////
    public async reload(): Promise<void> {
        await this.loadMemberProviderRelationships();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(this.sorting.entries.length == 0) {
                this.sorting.entries = [
                    {
                        reference: 'name.full',
                        direction: SortDirection.Ascending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    ////****************** Functions ************************************************////
    protected saveRelationship(relationship: CD.MemberProviderRelationship | null = null): void {
        this.editedRelationship = relationship ? deepClone(relationship) : {};
        this.$relationshipModal.open();
    }

    protected editRelationship(relationship: CD.MemberProviderRelationship) {
        if(!relationship || !this.$relationshipModal) {
            return;
        }
        this.editedRelationship = deepClone(relationship);
        this.$relationshipModal.open();
    }

    protected closeRelationshipModal(): void {
        if(this.$relationshipForm.reset) {
            this.$relationshipForm.reset();
        }
        this.$relationshipModal.close();
    }

    protected handleRelationshipFormSuccess({ detail }: FormSuccessCustomEvent): void {
        this.$relationshipModal.close();

        if(detail.form && detail.form.reset) {
            detail.form.reset();
        }

        this.reload();
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMemberProviderRelationships(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();

            const response = await this.service.loadMemberProviderRelationships(options);
            this.relationships = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateRelationship(relationship: CD.MemberProviderRelationship): Promise<void> {
        if(!relationship.id) return;

        // Show confirmation dialog only if deactivating
        if(relationship.isActive) {
            const result = await confirmation.showDeactivate('cd:memberProviderRelationship.$', 1, false);
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }

            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        try {
            this.state.beginSave();
            const response = await this.service.activateMemberProviderRelationship(relationship.id, !relationship.isActive);
            if(response.isSuccessful) {
                this.reload();
                this.toaster.standard.activate('cd:memberProviderRelationship.$', !relationship.isActive, response.isSuccessful);
            }
        }
        catch(error) {
            this.toaster.standard.activate('cd:memberProviderRelationship.$', !relationship.isActive, false);
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }
}
